import React from 'react';
import { cn } from '../lib/utils';
import { ArticleBody } from './ArticleBody';
import { AnchorLink, type LinkComponent } from './LinkComponent';
import { formatDate, tableOfContents } from './seo';
import type { BlogPost } from './types';

const Toc: React.FC<{ post: BlogPost; className?: string }> = ({ post, className }) => {
  const entries = tableOfContents(post);
  return (
    <nav aria-label="In this guide" className={className}>
      <ol className="m-0 p-0 list-none flex flex-col gap-1">
        {entries.map((e) => (
          <li key={e.id}>
            <a
              href={`#${e.id}`}
              className={cn(
                'block no-underline leading-snug py-1 transition-colors hover:text-ink',
                e.level === 2 ? 'text-[14px] font-semibold text-ink-soft' : 'text-[13px] text-ink-muted pl-3',
              )}
            >
              {e.text}
            </a>
          </li>
        ))}
      </ol>
    </nav>
  );
};

const Breadcrumb: React.FC<{ post: BlogPost; Link: LinkComponent }> = ({ post, Link }) => (
  <nav aria-label="Breadcrumb" className="text-[13px] text-ink-muted">
    <ol className="m-0 p-0 list-none flex items-center gap-2">
      <li>
        <Link to="/" className="no-underline text-ink-muted hover:text-ink transition-colors">
          Home
        </Link>
      </li>
      <li aria-hidden>/</li>
      <li>
        <Link to="/blog" className="no-underline text-ink-muted hover:text-ink transition-colors">
          Blog
        </Link>
      </li>
      <li aria-hidden>/</li>
      <li className="text-ink-soft">{post.category}</li>
    </ol>
  </nav>
);

/**
 * The whole article page below the site header: hero, sticky table of
 * contents and the body. Pure so the prerender can snapshot it.
 */
export const PostArticle: React.FC<{ post: BlogPost; Link?: LinkComponent; related?: BlogPost[] }> = ({
  post,
  Link = AnchorLink,
  related = [],
}) => (
  <article className="bg-bg">
    <header className="pt-28 md:pt-36 pb-10 md:pb-12 px-5 md:px-8">
      <div className="max-w-6xl mx-auto">
        <div className="max-w-3xl">
          <Breadcrumb post={post} Link={Link} />
          <h1 className="mt-5 font-display font-bold text-[34px] sm:text-[42px] md:text-[52px] leading-[1.06] tracking-[-0.03em] text-ink m-0">
            {post.title}
          </h1>
          <p className="mt-5 text-[17px] md:text-[20px] leading-relaxed text-ink-muted m-0">{post.description}</p>
          <div className="mt-7 flex flex-wrap items-center gap-x-3 gap-y-2 text-[14px] text-ink-muted">
            <span className="inline-flex items-center gap-2.5">
              <span className="w-8 h-8 rounded-full bg-accent text-white font-bold text-[13px] flex items-center justify-center" aria-hidden>
                {post.author.name.slice(0, 1)}
              </span>
              <span>
                <span className="font-semibold text-ink">{post.author.name}</span>
                <span className="hidden sm:inline"> · {post.author.role}</span>
              </span>
            </span>
            <span aria-hidden>·</span>
            <time dateTime={post.updatedAt}>Updated {formatDate(post.updatedAt)}</time>
            <span aria-hidden>·</span>
            <span>{post.readingMinutes} min read</span>
          </div>
        </div>
      </div>
    </header>

    <div className="px-5 md:px-8 pb-20 md:pb-28">
      <div className="max-w-6xl mx-auto lg:grid lg:grid-cols-[240px_minmax(0,1fr)] lg:gap-14 xl:gap-20">
        <aside className="hidden lg:block">
          <div className="sticky top-28">
            <p className="m-0 mb-3 text-[11px] font-bold uppercase tracking-[0.14em] text-ink-muted">In this guide</p>
            <Toc post={post} className="max-h-[calc(100vh-10rem)] overflow-y-auto pr-2 custom-scrollbar" />
          </div>
        </aside>

        <div className="max-w-[760px] min-w-0">
          <details className="lg:hidden mb-8 rounded-[18px] bg-surface border-hairline px-5 py-4 group">
            <summary className="cursor-pointer list-none flex items-center justify-between font-display font-bold text-[16px] text-ink [&::-webkit-details-marker]:hidden">
              In this guide
              <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round" className="transition-transform group-open:rotate-180" aria-hidden>
                <polyline points="6 9 12 15 18 9" />
              </svg>
            </summary>
            <Toc post={post} className="mt-3" />
          </details>

          <ArticleBody blocks={post.body} />

          {related.length ? (
            <section className="mt-16 pt-10 border-t-hairline">
              <p className="m-0 mb-5 font-display font-bold text-[22px] tracking-[-0.02em] text-ink">More from the blog</p>
              <ul className="m-0 p-0 list-none flex flex-col gap-3">
                {related.map((r) => (
                  <li key={r.slug}>
                    <Link to={`/blog/${r.slug}`} className="block no-underline rounded-[18px] bg-surface border-hairline shadow-card p-5 hover:border-[color:var(--hairline-strong)] transition-colors">
                      <span className="block text-[11px] font-bold uppercase tracking-[0.14em] text-ink-muted">{r.category}</span>
                      <span className="block mt-1.5 font-display font-bold text-[18px] tracking-[-0.01em] text-ink">{r.title}</span>
                      <span className="block mt-1.5 text-[14px] text-ink-muted">{r.readingMinutes} min read</span>
                    </Link>
                  </li>
                ))}
              </ul>
            </section>
          ) : null}
        </div>
      </div>
    </div>
  </article>
);

export default PostArticle;
